import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Fetch the source URL for a news post, extract main article paragraphs
 * from the HTML, and return them so readers can consume the full story
 * inside our app without leaving for the source site.
 */

const FETCH_TIMEOUT_MS = 10_000;
const MAX_HTML_BYTES = 2_500_000;
const MAX_PARAGRAPHS = 60;

const inputSchema = z.object({ slug: z.string().min(1).max(200) });

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&rsquo;/gi, "'")
    .replace(/&lsquo;/gi, "'")
    .replace(/&ldquo;/gi, '"')
    .replace(/&rdquo;/gi, '"')
    .replace(/&hellip;/gi, "…")
    .replace(/&mdash;/gi, "—")
    .replace(/&ndash;/gi, "–")
    .replace(/&#(\d+);/g, (_, n) => {
      try { return String.fromCodePoint(Number(n)); } catch { return ""; }
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => {
      try { return String.fromCodePoint(parseInt(n, 16)); } catch { return ""; }
    });
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, "");
}

/** Very small readability-lite: pull <article> or largest <p>-cluster. */
function extractArticleParagraphs(html: string): string[] {
  // Prefer <article>…</article> or main content, else full body.
  let scope = html;
  const artMatch = html.match(/<article\b[^>]*>([\s\S]*?)<\/article>/i);
  if (artMatch) scope = artMatch[1];
  else {
    const mainMatch = html.match(/<main\b[^>]*>([\s\S]*?)<\/main>/i);
    if (mainMatch) scope = mainMatch[1];
  }

  // Strip scripts, styles, nav/footer/aside/figure captions.
  scope = scope
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<nav[\s\S]*?<\/nav>/gi, "")
    .replace(/<footer[\s\S]*?<\/footer>/gi, "")
    .replace(/<aside[\s\S]*?<\/aside>/gi, "")
    .replace(/<figure[\s\S]*?<\/figure>/gi, "")
    .replace(/<form[\s\S]*?<\/form>/gi, "");

  const paragraphs: string[] = [];
  const pRe = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
  let m: RegExpExecArray | null;
  while ((m = pRe.exec(scope)) && paragraphs.length < MAX_PARAGRAPHS) {
    const text = decodeEntities(stripTags(m[1])).replace(/\s+/g, " ").trim();
    if (text.length < 40) continue;
    // Skip common boilerplate cues
    if (/^(share|subscribe|follow|advert|read more|related|copyright|cookie|sign up)/i.test(text)) continue;
    paragraphs.push(text);
  }
  return paragraphs;
}

function extractTitle(html: string): string | null {
  const og = html.match(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)["']/i);
  if (og) return decodeEntities(og[1]).trim();
  const t = html.match(/<title>([\s\S]*?)<\/title>/i);
  return t ? decodeEntities(stripTags(t[1])).trim() : null;
}

async function fetchHtml(url: string): Promise<string> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      redirect: "follow",
      signal: ctrl.signal,
      headers: {
        "user-agent":
          "Mozilla/5.0 (compatible; NkadimengDannyLegal-Reader/1.0; +https://nkadimengdannylcs.com)",
        accept: "text/html,application/xhtml+xml",
      },
    });
    if (!res.ok) throw new Error(`Source responded ${res.status}`);
    // Read up to MAX_HTML_BYTES
    const reader = res.body?.getReader();
    if (!reader) return await res.text();
    const chunks: Uint8Array[] = [];
    let total = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      chunks.push(value);
      if (total >= MAX_HTML_BYTES) {
        try { await reader.cancel(); } catch {}
        break;
      }
    }
    return new TextDecoder("utf-8", { fatal: false }).decode(
      Buffer.concat(chunks.map((c) => Buffer.from(c))),
    );
  } finally {
    clearTimeout(timer);
  }
}

export const getFullNewsContent = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => inputSchema.parse(i))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: post, error } = await supabaseAdmin
      .from("news_posts")
      .select("id, slug, source_url, is_published")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post || !post.source_url) {
      return { ok: false as const, reason: "no-source" };
    }

    try {
      const html = await fetchHtml(post.source_url);
      const paragraphs = extractArticleParagraphs(html);
      const sourceTitle = extractTitle(html);
      if (paragraphs.length === 0) {
        return { ok: false as const, reason: "no-content" };
      }
      return {
        ok: true as const,
        sourceUrl: post.source_url,
        sourceTitle,
        paragraphs,
      };
    } catch (e: any) {
      console.error("[getFullNewsContent]", e);
      return { ok: false as const, reason: "fetch-failed" };
    }
  });
